/**
 * 계약의뢰 첨부 — 인앱 문서 뷰어
 * 지원: PDF · 이미지 · TXT · HWP/HWPX(rhwp)
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import { theme } from 'styles/theme';
import {
  getFileExt,
  isViewableExt,
  loadContractRequestFile,
} from 'expense/contractRequestFileStore';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 6px;
`;

const Status = styled.p`
  margin: 0;
  flex: 0 0 auto;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

const ViewerHost = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const Frame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const ImgWrap = styled.div`
  position: absolute;
  inset: 0;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12px;
  background: ${theme.colors.background.light};
`;

const PreviewImg = styled.img`
  max-width: 100%;
  height: auto;
  object-fit: contain;
`;

const TextPre = styled.pre`
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 12px;
  overflow: auto;
  font-size: ${theme.typography.fontSize.sm};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${theme.colors.text.primary};
`;

const ErrorBox = styled.div`
  padding: 24px 16px;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  text-align: center;
`;

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   docUid?: string,
 *   fileName?: string,
 *   prefsUserId?: string,
 * }} props
 */
export default function ContractRequestDocViewerDialog({
  open,
  onClose,
  docUid = '',
  fileName = '',
  prefsUserId = '',
}) {
  const hwpHostRef = useRef(null);
  const editorRef = useRef(null);
  const objectUrlRef = useRef('');
  const hwpBlobRef = useRef(null);
  const [status, setStatus] = useState('준비 중…');
  const [mode, setMode] = useState(''); // pdf | image | text | hwp | unsupported | error
  const [objectUrl, setObjectUrl] = useState('');
  const [textContent, setTextContent] = useState('');
  const [hwpName, setHwpName] = useState('');

  // 파일 로드 (PDF/이미지/TXT/메타)
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const revoke = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
    };

    (async () => {
      revoke();
      hwpBlobRef.current = null;
      setObjectUrl('');
      setTextContent('');
      setHwpName('');
      setMode('');
      setStatus('파일 로딩…');

      try {
        const uid = String(docUid || '').trim();
        if (!uid) throw new Error('문서 UID가 없습니다.');

        const rec = await loadContractRequestFile(uid);
        if (cancelled) return;
        if (!rec?.blob) throw new Error('저장된 파일이 없습니다. 다시 업로드하세요.');

        const name = rec.fileName || fileName || 'document';
        const ext = rec.fileExt || getFileExt(name);

        if (!isViewableExt(ext)) {
          setMode('unsupported');
          setStatus(`열람 미지원 확장자: .${ext || '?'}`);
          return;
        }

        if (ext === 'pdf') {
          const url = URL.createObjectURL(rec.blob);
          objectUrlRef.current = url;
          setObjectUrl(url);
          setMode('pdf');
          setStatus(`${name} · PDF 뷰어`);
          return;
        }

        if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
          const url = URL.createObjectURL(rec.blob);
          objectUrlRef.current = url;
          setObjectUrl(url);
          setMode('image');
          setStatus(`${name} · 이미지`);
          return;
        }

        if (ext === 'txt') {
          const text = await rec.blob.text();
          if (cancelled) return;
          setTextContent(text);
          setMode('text');
          setStatus(`${name} · 텍스트`);
          return;
        }

        if (ext === 'hwp' || ext === 'hwpx') {
          hwpBlobRef.current = rec.blob;
          setHwpName(name);
          setMode('hwp');
          setStatus(`${name} · HWP 로딩 준비…`);
          return;
        }

        setMode('unsupported');
        setStatus(`열람 미지원: .${ext}`);
      } catch (e) {
        console.error('[ContractRequestDocViewer]', e);
        if (!cancelled) {
          setMode('error');
          setStatus(`열람 실패: ${e?.message || e}`);
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
  }, [open, docUid, fileName]);

  // HWP — host 마운트 후 rhwp 로드
  useEffect(() => {
    if (!open || mode !== 'hwp' || !hwpBlobRef.current) return undefined;

    let cancelled = false;

    const destroyHwp = () => {
      if (editorRef.current?.destroy) {
        try {
          editorRef.current.destroy();
        } catch {
          /* ignore */
        }
      }
      editorRef.current = null;
    };

    (async () => {
      destroyHwp();
      setStatus('HWP 에디터 로딩…');
      try {
        if (!hwpHostRef.current) {
          await new Promise((r) => requestAnimationFrame(() => r()));
        }
        if (cancelled || !hwpHostRef.current) {
          throw new Error('뷰어 영역을 준비하지 못했습니다.');
        }

        const mod = await import('@rhwp/editor');
        if (cancelled || !hwpHostRef.current) return;

        const editor = await mod.createEditor(hwpHostRef.current, {
          width: '100%',
          height: '100%',
        });
        if (cancelled) {
          editor.destroy?.();
          return;
        }
        editorRef.current = editor;

        const name = hwpName || fileName || 'document.hwp';
        const buf = new Uint8Array(await hwpBlobRef.current.arrayBuffer());
        if (cancelled) return;
        const result = await editor.loadFile(buf, name, { suppressDialogs: true });
        if (cancelled) return;
        setStatus(`${name} · HWP · pages=${result?.pageCount ?? '?'}`);
      } catch (e) {
        console.error('[ContractRequestDocViewer/hwp]', e);
        if (!cancelled) {
          setMode('error');
          setStatus(`HWP 열람 실패: ${e?.message || e}`);
        }
      }
    })();

    return () => {
      cancelled = true;
      destroyHwp();
    };
  }, [open, mode, hwpName, fileName]);

  const title = fileName ? `문서 열람 — ${fileName}` : '문서 열람';

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title={title}
      dialogWidth="min(1100px, 96vw)"
      dialogHeight="min(820px, 92vh)"
      keepMounted={false}
      prefsUserId={prefsUserId}
      sizeKey="contractRequestDocViewer"
    >
      <Body>
        <Status>{status}</Status>
        <ViewerHost>
          {mode === 'pdf' && objectUrl ? <Frame title="PDF" src={objectUrl} /> : null}
          {mode === 'image' && objectUrl ? (
            <ImgWrap>
              <PreviewImg src={objectUrl} alt={fileName || 'preview'} />
            </ImgWrap>
          ) : null}
          {mode === 'text' ? <TextPre>{textContent}</TextPre> : null}
          {mode === 'hwp' ? (
            <div
              ref={hwpHostRef}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />
          ) : null}
          {mode === 'unsupported' || mode === 'error' ? <ErrorBox>{status}</ErrorBox> : null}
        </ViewerHost>
      </Body>
    </DraggableDialog>
  );
}
